import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { OpsTaskStatusId } from 'model/OpsTask';
import { FunctionComponent, useState } from 'react';
import styles from './OpsTaskStatusSelect.module.scss';

export interface OpsTaskStatusOption {
    id: OpsTaskStatusId;
    name: string;
}

interface OpsTaskStatusSelectProps {
    value: OpsTaskStatusId;
    options: Array<OpsTaskStatusOption>;
    pastDue?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    className?: string;
    onChange?: (status: OpsTaskStatusId) => void;
}

const variantClassName = (id: OpsTaskStatusId): string => {
    switch (id) {
        case 2:
            return styles.inProgress;
        case 3:
            return styles.completed;
        case 1:
        default:
            return styles.todo;
    }
};

export const OpsTaskStatusSelect: FunctionComponent<OpsTaskStatusSelectProps> = props => {
    const [open, setOpen] = useState(false);

    const selected = props.options.find(option => option.id === props.value);
    const overdue = Boolean(props.pastDue) && props.value !== 3;
    const label = overdue ? 'Past Due' : selected?.name ?? '—';

    const handleSelect = (id: OpsTaskStatusId) => {
        if (id !== props.value) {
            props.onChange?.(id);
        }
    };

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
        >
            <DropdownTrigger
                className={clsx(props.className, { [styles.fullWidthTrigger]: props.fullWidth })}
                disabled={props.disabled}
            >
                <div
                    className={clsx(styles.pill, overdue ? styles.pastDue : variantClassName(props.value), {
                        [styles.disabled]: props.disabled,
                        [styles.fullWidth]: props.fullWidth
                    })}
                >
                    <span className={styles.label}>{label}</span>
                    {!props.disabled && (
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={clsx(styles.chevron, { [styles.chevronOpen]: open })}
                        />
                    )}
                </div>
            </DropdownTrigger>

            <DropdownContent
                minWidth={170}
                align="start"
                sideOffset={6}
            >
                {props.options.map(option => (
                    <DropdownItem
                        key={option.id}
                        rightIcon={option.id === props.value ? faCheck : undefined}
                        onSelect={() => handleSelect(option.id)}
                    >
                        {option.name}
                    </DropdownItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );
};

export default OpsTaskStatusSelect;
